import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Card,
  CardBody,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
  Button,
  Input,
  Select,
  SelectItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@heroui/react'

import { useToast } from '../../../hooks'
import {
  getComprehensiveMediaMatching,
  type ComprehensiveMediaMatchingResult,
} from '../services/mediaManagementService'
import { getMediaUrls } from '../../gifs/services/mediaUrlService'

// Icons
const DocumentIcon = () => <span>📄</span>
const GifIcon = () => <span>🖼️</span>
const Mp4Icon = () => <span>🎬</span>
const CheckIcon = () => <span>✅</span>
const CrossIcon = () => <span>❌</span>
const WarningIcon = () => <span>⚠️</span>
const SearchIcon = () => <span>🔍</span>
const FilterIcon = () => <span>🔽</span>

type FilterType =
  | 'all'
  | 'complete'
  | 'missing-document'
  | 'missing-gif'
  | 'missing-mp4'
  | 'has-issues'

export const MediaMatchingTable: React.FC = () => {
  const [data, setData] = useState<ComprehensiveMediaMatchingResult | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')

  const toast = useToast()

  const loadData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await getComprehensiveMediaMatching()
      setData(result)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load data'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Filter and search logic
  const filteredEntries = useMemo(() => {
    if (!data) return []

    let filtered = data.entries

    // Apply filter
    switch (filterType) {
      case 'complete':
        filtered = filtered.filter(
          (entry) =>
            entry.documentExists && entry.gifFileExists && entry.mp4FileExists
        )
        break
      case 'missing-document':
        filtered = filtered.filter((entry) => !entry.documentExists)
        break
      case 'missing-gif':
        filtered = filtered.filter((entry) => !entry.gifFileExists)
        break
      case 'missing-mp4':
        filtered = filtered.filter((entry) => !entry.mp4FileExists)
        break
      case 'has-issues':
        filtered = filtered.filter((entry) => entry.issues.length > 0)
        break
      default:
        // 'all' - no filtering
        break
    }

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (entry) =>
          entry.gifId.toLowerCase().includes(searchLower) ||
          entry.gifFileName?.toLowerCase().includes(searchLower) ||
          entry.mp4FileName?.toLowerCase().includes(searchLower) ||
          entry.issues.some((issue) =>
            issue.toLowerCase().includes(searchLower)
          )
      )
    }

    return filtered
  }, [data, filterType, searchTerm])

  const getExistenceIcon = (exists: boolean) => {
    return exists ? (
      <span className="text-green-600 dark:text-green-400">
        <CheckIcon />
      </span>
    ) : (
      <span className="text-red-600 dark:text-red-400">
        <CrossIcon />
      </span>
    )
  }

  // Helper function to get GIF preview URL using the existing media URL service
  const getGifPreviewUrl = (gifId: string) => {
    // Use the storage path format that getMediaUrls expects
    const storagePath = `gifs/${gifId}.gif`
    const mediaUrls = getMediaUrls(storagePath)
    return mediaUrls.displayUrl
  }

  const filterOptions = [
    { key: 'all', label: 'All Entries' },
    { key: 'complete', label: 'Complete Matches' },
    { key: 'has-issues', label: 'Has Issues' },
    { key: 'missing-document', label: 'Missing Document' },
    { key: 'missing-gif', label: 'Missing GIF File' },
    { key: 'missing-mp4', label: 'Missing MP4 File' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Media Matching Table</h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm">
            Comprehensive view of how documents, GIF files, and MP4 files match
            up
          </p>
        </div>
        <Button
          onClick={loadData}
          isLoading={isLoading}
          variant="flat"
          size="sm"
        >
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card>
            <CardBody className="p-4 text-center">
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {data.summary.totalEntries.toLocaleString()}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Total Entries
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {data.summary.completeMatches.toLocaleString()}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Complete
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {data.summary.missingDocuments.toLocaleString()}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Missing Docs
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {data.summary.missingGifFiles.toLocaleString()}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Missing GIFs
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {data.summary.missingMp4Files.toLocaleString()}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Missing MP4s
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          placeholder="Search by GIF ID, filename, or issues..."
          value={searchTerm}
          onValueChange={setSearchTerm}
          startContent={<SearchIcon />}
          className="sm:max-w-xs"
          size="sm"
        />
        <Select
          placeholder="Filter entries"
          selectedKeys={[filterType]}
          onSelectionChange={(keys) =>
            setFilterType(Array.from(keys)[0] as FilterType)
          }
          startContent={<FilterIcon />}
          className="sm:max-w-xs"
          size="sm"
        >
          {filterOptions.map((option) => (
            <SelectItem key={option.key}>{option.label}</SelectItem>
          ))}
        </Select>
        {(searchTerm || filterType !== 'all') && (
          <Button
            variant="flat"
            size="sm"
            onPress={() => {
              setSearchTerm('')
              setFilterType('all')
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Results Count */}
      {data && (
        <div className="text-sm text-slate-600 dark:text-slate-400">
          Showing {filteredEntries.length.toLocaleString()} of{' '}
          {data.entries.length.toLocaleString()} entries
        </div>
      )}

      {/* Table */}
      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-2">
                <WarningIcon />
              </div>
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <Button onClick={loadData} variant="flat" size="sm">
                Retry
              </Button>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 dark:text-slate-400">
                {data?.entries.length === 0
                  ? 'No media entries found'
                  : 'No entries match the current filters'}
              </p>
            </div>
          ) : (
            <Table aria-label="Media matching table" className="min-h-[400px]">
              <TableHeader>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <DocumentIcon />
                    Document
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <GifIcon />
                    GIF File
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <Mp4Icon />
                    MP4 File
                  </div>
                </TableColumn>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => (
                  <TableRow key={entry.gifId}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getExistenceIcon(entry.documentExists)}
                        {entry.documentExists ? (
                          <div className="font-mono text-xs">{entry.gifId}</div>
                        ) : (
                          <div className="text-xs text-slate-400">
                            <div className="font-mono text-sm">
                              {entry.gifId}
                            </div>
                            <div className="text-slate-500">No document</div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getExistenceIcon(entry.gifFileExists)}
                        {entry.gifFileExists ? (
                          <Popover placement="right" showArrow>
                            <PopoverTrigger>
                              <div className="text-xs text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                <div>{entry.gifFileName}</div>
                              </div>
                            </PopoverTrigger>
                            <PopoverContent className="p-2">
                              <div className="flex flex-col items-center gap-2">
                                <div className="text-xs font-medium text-slate-700 dark:text-slate-300">
                                  {entry.gifFileName}
                                </div>
                                <img
                                  src={getGifPreviewUrl(entry.gifId)}
                                  alt={`Preview of ${entry.gifFileName}`}
                                  className="max-w-[200px] max-h-[200px] rounded-lg shadow-lg"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.style.display = 'none'
                                    const errorDiv =
                                      document.createElement('div')
                                    errorDiv.className =
                                      'text-red-500 text-xs p-4'
                                    errorDiv.textContent = 'Failed to load GIF'
                                    target.parentNode?.appendChild(errorDiv)
                                  }}
                                />
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getExistenceIcon(entry.mp4FileExists)}
                        {entry.mp4FileExists && (
                          <div className="text-xs text-slate-600 dark:text-slate-400">
                            <div>{entry.mp4FileName}</div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default MediaMatchingTable
